import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Button,
  Flex,
  H1,
  P,
  PasswordField,
} from '@decisiv/ui-components';

import {
  ButtonsContainer,
  InnerContainer,
  StyledAlert,
  StyledTextField as TextField,
} from './styledComponents';
import PasswordRequirements from './PasswordRequirements';

function Form({ dealer, csrfToken, onSuccessEmailChanged }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: dealer.email,
    password1: '',
  });
  const [validPassword, setValidPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [validEmail, setValidEmail] = useState(false);
  const [error, setError] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const onClickHandler = () => {
    setButtonDisabled(true);

    fetch(`/vendors/update_user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ user: formData }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response);
        }

        if (dealer.email === formData.email) {
          window.location.href = '/login';
        } else {
          onSuccessEmailChanged(formData.email);
        }
      })
      .catch((e) => {
        /* eslint-disable no-console */
        console.error(e);
        setError(true);
      });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(formData.email);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [formData.email]);

  useEffect(() => {
    if (debouncedEmail === '') return;

    fetch(`/vendors/check_email?email=${debouncedEmail}`, {
      method: 'GET',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        setEmailError(data.message || '');
        setValidEmail(data.valid);
      })
      .catch((err) => {
        /* eslint-disable no-console */
        console.error(err);
        setError(true);
      });
  }, [csrfToken, debouncedEmail]);

  useEffect(() => {
    // Enables button if form is valid
    const values = Object.values(formData);
    if (values.every((value) => value !== '') && validPassword && validEmail) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [formData, validEmail, validPassword]);

  return (
    <InnerContainer>
      <H1
        style={{
          textAlign: 'center',
          margin: '0',
          padding: '0',
        }}
      >
        Welcome to {dealer.portalName}
      </H1>
      <P
        fontWeight={500}
        shade={1}
        style={{
          textAlign: 'center',
          marginBottom: '40px',
        }}
      >
        Collaborate with {dealer.owner.name} in managing repairs efficiently.
        <br />
        Get started by configuring your profile:
      </P>

      <Flex
        flexDirection="column"
        style={{
          width: '360px',
        }}
      >
        {error && (
          <StyledAlert>
            <Alert intent="danger" title="Something went wrong. Try again." />
          </StyledAlert>
        )}

        <TextField
          label="First Name"
          name="first_name"
          required
          value={formData.first_name}
          onChange={(e) =>
            setFormData({ ...formData, first_name: e.target.value })
          }
        />

        <TextField
          label="Last Name"
          name="last_name"
          required
          value={formData.last_name}
          onChange={(e) =>
            setFormData({ ...formData, last_name: e.target.value })
          }
        />

        <TextField
          label="Email"
          name="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          warningMessage={emailError}
        />

        <PasswordField
          marginBottom={1}
          name="password"
          required
          autoComplete="off"
          value={formData.password1}
          onChange={(e) =>
            setFormData({ ...formData, password1: e.target.value })
          }
        />

        <PasswordRequirements
          password={formData.password1}
          onValidate={setValidPassword}
        />

        <ButtonsContainer>
          <div className="btn__element--login-btn">
            <Button
              text="Continue"
              onClick={onClickHandler}
              disabled={buttonDisabled}
            />
          </div>
        </ButtonsContainer>
      </Flex>
    </InnerContainer>
  );
}

Form.propTypes = {
  dealer: PropTypes.shape({
    portalName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    owner: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  csrfToken: PropTypes.string.isRequired,
  onSuccessEmailChanged: PropTypes.func.isRequired,
};

export default Form;
